<template>
  <div class="tw-flex tw-align-middle tw-gap-6">
    <span class="tw-w-[320px]">
      <cl-searchable-dropdown
        :placeholder="t('all-customers-menu-item')"
        :no-results="t('no-search-results-default')"
        :options="customerOptions"
        v-model:value="selectedCustomer"
        v-model:search="searchTerm"
        :is-fetching="isRequestPending"
        :has-more="isMore"
        @load-more="onLoadMore"
      />
    </span>
    <span class="tw-w-[164px]">
      <cl-select v-model="selectedMonth" :placeholder="t('last-12-months')">
        <cl-select-item
          v-for="option in monthOptions"
          :value="option.value"
          :textValue="option.text"
        >
          {{ option.text }}</cl-select-item
        >
      </cl-select>
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useTrendChart } from "../trend-chart.state";
import { useI18n } from "vue-i18n";
import { useDebounceFn } from "@vueuse/core";
import type { Option } from "@/modules/SecurityAwarnessTraining/types/trend-chart";
import {
  useRequestStatus,
  PENDING,
  READY,
  ERROR,
} from "@/composables/useRequestStatus";
import { trackEvent } from "@/utils/track-event";

const { customers, getCustomers, getMoreCustomers, isMore } = useTrendChart();
const { t } = useI18n();
const { setRequestStatus, isRequestPending } = useRequestStatus({
  type: PENDING,
});

const selectedCustomer = defineModel<Option | null>("selectedCustomer");
const selectedMonth = defineModel<string | null>("selectedMonth");
const searchTerm = ref<string>("");
const currentPage = ref<number>(1);

const customerOptions = computed<Option[]>(() =>
  customers.value.map((customer) => ({
    text: customer.customerName,
    value: customer.customerOrganisationID,
  })),
);

const monthOptions = [
  { text: t("last-12-months"), value: "Year" },
  { text: t("january"), value: "Jan" },
  { text: t("february"), value: "Feb" },
  { text: t("march"), value: "Mar" },
  { text: t("april"), value: "Apr" },
  { text: t("may"), value: "May" },
  { text: t("june"), value: "Jun" },
  { text: t("july"), value: "Jul" },
  { text: t("august"), value: "Aug" },
  { text: t("september"), value: "Sep" },
  { text: t("october"), value: "Oct" },
  { text: t("november"), value: "Nov" },
  { text: t("december"), value: "Dec" },
];

const onLoadMore = async () => {
  if (!isRequestPending.value) {
    setRequestStatus({ type: PENDING });
    try {
      currentPage.value += 1;
      await getMoreCustomers({
        params: {
          filter: searchTerm.value,
          results: "5",
          page: currentPage.value,
        },
      });
      setRequestStatus({ type: READY });
    } catch {
      setRequestStatus({ type: ERROR });
    }
  }
};

const fetchCustomers = useDebounceFn(
  async (params: Record<string, unknown>) => {
    setRequestStatus({ type: PENDING });
    try {
      await getCustomers({
        params: {
          ...params,
        },
      });

      setRequestStatus({ type: READY });
    } catch {
      setRequestStatus({ type: ERROR });
      isMore.value = false;
    }
  },
  500,
);

watch(searchTerm, async () => {
  currentPage.value = 1;
  const params = {
    filter: searchTerm.value,
    results: "5",
    page: currentPage.value,
  };
  await fetchCustomers(params);
});

watch(selectedCustomer, () => {
  trackEvent({
    category: "SAT MSP Trend Chart",
    action: "customer selected",
    name: "",
  });
});

watch(selectedMonth, () => {
  trackEvent({
    category: "SAT MSP Trend Chart",
    action: "period selected",
    name: "",
  });
});

onMounted(async () => {
  const params = {
    results: "5",
    page: "1",
  };
  await fetchCustomers(params);
});
</script>
